import React, { useState, useEffect } from 'react';
import { Modal, Table, Select, Button, Space, Typography, Tag, message, Row, Col, Divider } from 'antd';
import {
    TagsOutlined,
    SaveOutlined,
    ArrowRightOutlined,
    GlobalOutlined,
    CheckCircleOutlined,
    BulbOutlined,
    GroupOutlined,
    FolderOpenOutlined
} from '@ant-design/icons';
import { Method_POST_CONTEXTO } from '../../../../../service/api';

const { Text } = Typography;

export default function ReclassificacaoModal({ open, onClose, onConfirm, batchMode }) {
    const [loading, setLoading] = useState(false);
    
    // Estados para Mapeamento OF
    const [openFinanceCategories, setOpenFinanceCategories] = useState([]);
    const [systemCategories, setSystemCategories] = useState([]);
    const [mappings, setMappings] = useState({});

    // Estados para Reclassificação em Lote (Batch)
    const [grupos, setGrupos] = useState([]);
    const [categorias, setCategorias] = useState([]);
    const [selectedGrupo, setSelectedGrupo] = useState(null);
    const [selectedCategoria, setSelectedCategoria] = useState(null);

    useEffect(() => {
        if (open) {
            if (batchMode) {
                fetchGroups();
            } else {
                fetchData();
            }
        } else {
            // Resetar estados ao fechar
            setSelectedGrupo(null);
            setSelectedCategoria(null);
            setCategorias([]);
        }
    }, [open, batchMode]);

    const fetchGroups = async () => {
        setLoading(true);
        try {
            const res = await Method_POST_CONTEXTO("/contabil/lancamento/categoria/grupo/listar", {});
            setGrupos(Array.isArray(res) ? res : []);
        } catch (error) {
            console.error("Erro ao carregar grupos:", error);
            message.error("Erro ao carregar grupos de categorias.");
        } finally {
            setLoading(false);
        }
    };

    const handleGrupoChange = async (grupoId) => {
        setSelectedGrupo(grupoId);
        setSelectedCategoria(null);
        setCategorias([]);
        
        if (!grupoId) return;

        setLoading(true);
        try {
            const res = await Method_POST_CONTEXTO("/contabil/lancamento/categoria/listar", {
                grupo: { id: grupoId },
                situacao: { id: "A" }
            });
            setCategorias(Array.isArray(res) ? res : []);
        } catch (error) {
            console.error("Erro ao carregar categorias:", error);
            message.error("Erro ao carregar categorias do grupo.");
        } finally {
            setLoading(false);
        }
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const [ofRes, sysRes] = await Promise.all([
                Method_POST_CONTEXTO("/contabil/categoria-open-finance/listar", {}),
                Method_POST_CONTEXTO("/contabil/lancamento/categoria/listar", { situacao: { id: "A" } })
            ]);

            setOpenFinanceCategories(Array.isArray(ofRes) ? ofRes : []);
            setSystemCategories(Array.isArray(sysRes) ? sysRes : []);

            const initialMappings = {};
            if (Array.isArray(ofRes)) {
                ofRes.forEach(cat => {
                    if (cat.categoriaSistemaId) {
                        initialMappings[cat.id] = cat.categoriaSistemaId;
                    }
                });
            }
            setMappings(initialMappings);
        } catch (error) {
            console.error("Erro ao carregar dados de reclassificação:", error);
            message.error("Erro ao carregar categorias.");
        } finally {
            setLoading(false);
        }
    };

    const handleMappingChange = (ofId, sysId) => {
        setMappings(prev => ({
            ...prev,
            [ofId]: sysId
        }));
    };

    const handleSaveMapping = async () => {
        setLoading(true);
        try {
            const payload = Object.entries(mappings).map(([ofId, sysId]) => ({
                id: ofId,
                categoriaSistemaId: sysId
            }));

            await Method_POST_CONTEXTO("/contabil/categoria-open-finance/vincular", payload);
            message.success("Mapeamento de categorias salvo com sucesso!");
            onClose();
        } catch (error) {
            console.error("Erro ao salvar mapeamento:", error);
            message.error("Erro ao salvar alterações.");
        } finally {
            setLoading(false);
        }
    };

    const handleBatchConfirm = () => {
        if (!selectedCategoria) {
            message.warning("Por favor, selecione uma categoria.");
            return;
        }
        onConfirm(selectedCategoria);
    };

    const columns = [
        {
            title: 'Categoria Open Finance',
            dataIndex: 'nome',
            key: 'nome',
            render: (text) => (
                <Space>
                    <GlobalOutlined style={{ color: '#005A9E' }} />
                    <Text strong>{text}</Text>
                </Space>
            )
        },
        {
            title: '',
            key: 'arrow',
            width: 50,
            align: 'center',
            render: () => <ArrowRightOutlined style={{ color: '#94a3b8' }} />
        },
        {
            title: 'Categoria do Sistema',
            key: 'systemCategory',
            render: (_, record) => (
                <Select
                    showSearch
                    placeholder="Selecione uma categoria"
                    style={{ width: '100%' }}
                    value={mappings[record.id]}
                    onChange={(val) => handleMappingChange(record.id, val)}
                    optionFilterProp="children"
                    className="premium-select"
                >
                    {systemCategories.map(cat => (
                        <Select.Option key={cat.id} value={cat.id}>
                            <Tag color={cat.tipo?.id === 'C' ? 'success' : 'error'} style={{ marginRight: 8, fontSize: '10px' }}>
                                {cat.tipo?.id === 'C' ? 'Receita' : 'Despesa'}
                            </Tag>
                            {cat.nome}
                        </Select.Option>
                    ))}
                </Select>
            )
        }
    ];

    const renderBatchUI = () => (
        <div style={{ padding: '8px 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24, padding: '16px', background: '#eff6ff', borderRadius: '12px', border: '1px solid #bfdbfe' }}>
                <BulbOutlined style={{ fontSize: 24, color: '#2563eb' }} />
                <div>
                    <Text strong style={{ color: '#1e40af', display: 'block' }}>Reclassificação em Lote</Text>
                    <Text type="secondary" style={{ fontSize: '13px' }}>Selecione abaixo o novo grupo e categoria para aplicar aos lançamentos selecionados.</Text>
                </div>
            </div>

            <Row gutter={[24, 24]}>
                <Col span={24}>
                    <div style={{ marginBottom: 8 }}>
                        <Text strong><GroupOutlined /> Grupo de Categoria</Text>
                    </div>
                    <Select
                        showSearch
                        placeholder="Selecione o grupo"
                        style={{ width: '100%' }}
                        size="large"
                        className="premium-select"
                        onChange={handleGrupoChange}
                        value={selectedGrupo}
                        optionFilterProp="children"
                    >
                        {grupos.map(g => (
                            <Select.Option key={g.id} value={g.id}>{g.nome}</Select.Option>
                        ))}
                    </Select>
                </Col>

                <Col span={24}>
                    <div style={{ marginBottom: 8 }}>
                        <Text strong><FolderOpenOutlined /> Categoria / Conta</Text>
                    </div>
                    <Select
                        showSearch
                        placeholder={selectedGrupo ? "Selecione a categoria" : "Selecione primeiro um grupo"}
                        style={{ width: '100%' }}
                        size="large"
                        className="premium-select"
                        disabled={!selectedGrupo || categorias.length === 0}
                        onChange={setSelectedCategoria}
                        value={selectedCategoria}
                        optionFilterProp="children"
                        loading={loading}
                    >
                        {categorias.map(c => (
                            <Select.Option key={c.id} value={c.id}>{c.nome}</Select.Option>
                        ))}
                    </Select>
                </Col>
            </Row>

            <Divider />
            
            <div style={{ textAlign: 'center', padding: '12px', background: '#f8fafc', borderRadius: '8px', border: '1px dashed #cbd5e1' }}>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                    Esta ação atualizará permanentemente a categoria dos itens selecionados na tabela principal.
                </Text>
            </div>
        </div>
    );

    const renderMappingUI = () => (
        <>
            <div style={{ marginBottom: 20 }}>
                <Text type="secondary" style={{ fontSize: '14px' }}>
                    Vincule as categorias recebidas pelo Open Finance às categorias correspondentes do seu sistema para automatizar a classificação dos lançamentos.
                </Text>
            </div>

            <Table
                dataSource={openFinanceCategories}
                columns={columns}
                rowKey="id"
                loading={loading}
                pagination={{ pageSize: 8, size: 'small' }}
                size="middle"
                className="premium-table"
                scroll={{ x: 600 }}
            />
        </>
    );

    return (
        <Modal
            title={
                <Space>
                    <TagsOutlined style={{ color: '#2563eb' }} />
                    <span style={{ fontWeight: 700 }}>{batchMode ? 'Reclassificar Lançamentos' : 'Mapeamento Open Finance'}</span>
                </Space>
            }
            open={open}
            onCancel={onClose}
            width={batchMode ? 500 : 750}
            className="premium-modal-v2 reclassificacao-modal"
            centered
            footer={[
                <Button key="cancel" onClick={onClose} className="premium-btn-secondary">
                    Cancelar
                </Button>,
                <Button
                    key="save"
                    type="primary"
                    icon={batchMode ? <CheckCircleOutlined /> : <SaveOutlined />}
                    onClick={batchMode ? handleBatchConfirm : handleSaveMapping}
                    loading={loading}
                    className="premium-btn-primary"
                    disabled={batchMode && !selectedCategoria}
                >
                    {batchMode ? 'Confirmar Reclassificação' : 'Salvar Mapeamento'}
                </Button>
            ]}
        >
            {batchMode ? renderBatchUI() : renderMappingUI()}
        </Modal>
    );
}
